import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { MicrosoftOutlookLogo, Warning, Lock } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

export default function Login() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("sso"); // sso | local
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (user) redirectByRole(user.role);
  }, []);

  const redirectByRole = (role) => {
    if (role === "vendedor") navigate("/vendedor");
    else navigate("/hedge");
  };

  const handleMicrosoftLogin = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/microsoft/authorize-url");
      window.location.href = data.url;
    } catch (e) {
      toast.error("Microsoft SSO não configurado no servidor");
      setLoading(false);
    }
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/local-login", { email, password });
      localStorage.setItem("brf_token", data.access_token);
      localStorage.setItem("brf_user", JSON.stringify(data.user));
      toast.success(`Bem-vindo, ${data.user.nome}`);
      window.location.href = data.user.role === "vendedor" ? "/vendedor" : "/hedge";
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Credenciais inválidas");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#061729]">
      <div
        className="relative hidden md:flex flex-col justify-between p-8 pt-6"
        style={{
          backgroundImage:
            "url('/imagem2.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[#061729]/75" />
        <div className="relative z-10 flex flex-col items-start">
          <img src="/logo2.png" alt="BRFertil" className="w-28 h-28 object-contain" />
          <p className="text-[10px] tracking-[0.25em] text-[#8aa9c9] uppercase -mt-6">Sistema de Trava de Câmbio</p>
        </div>

        <div className="relative z-10 space-y-6">
          <h2 className="text-5xl font-bold tracking-tight leading-tight">
            Precisão em cada <span className="text-[#40B6A2]">trava</span>.<br />
            Controle em cada <span className="text-[#40B6A2]">operação</span>.
          </h2>
          <p className="text-[#b8cde2] text-base max-w-md leading-relaxed">
            Plataforma inteligente para gestão cambial, com dados em tempo real e automação integrada ao fluxo comercial.
          </p>
          <div className="flex gap-6 font-mono-data text-xs pt-4">
            <div>
              <p className="text-[#6b89a8] uppercase tracking-widest mb-1">Atualização</p>
              <p className="text-[#40B6A2] font-semibold">Tempo Real</p>
            </div>
            <div>
              <p className="text-[#6b89a8] uppercase tracking-widest mb-1">Horário</p>
              <p className="text-white">09:30 - 16:30</p>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-xs text-[#6b89a8] tracking-wider">
          BRFERTIL © 2026 · Desenvolvido por <a href="https://www.linkedin.com/in/murilo-fernandes-de-almeida-20141622b/" target="_blank" rel="noopener noreferrer" className="text-[#40B6A2] hover:no-underline no-underline" style={{textDecoration:"none"}}>Murilo Dev</a>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-6">
          <div className="flex md:hidden justify-center mb-4">
            <img src="/globo.png" alt="BRFertil" className="w-28 h-28 object-contain" />
          </div>
          <div>
            <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase mb-3">Autenticação</p>
            <h1 className="text-3xl font-bold tracking-tight mb-2">Entre na plataforma</h1>
            <p className="text-sm text-[#8aa9c9]">
              {mode === "sso" ? "Acesse com sua conta Microsoft corporativa." : "Login administrativo local."}
            </p>
          </div>

          {mode === "sso" ? (
            <div className="space-y-4">
              <button
                onClick={handleMicrosoftLogin}
                disabled={loading}
                data-testid="btn-microsoft-sso"
                className="w-full py-3 px-4 bg-white text-black font-medium text-sm rounded-sm hover:bg-zinc-100 transition-colors flex items-center justify-center gap-3 disabled:opacity-60"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 23 23"><path fill="#000" d="M0 0h11v11H0z"/><path fill="#000" d="M12 0h11v11H12z"/><path fill="#000" d="M0 12h11v11H0z"/><path fill="#000" d="M12 12h11v11H12z"/></svg>
                {loading ? "Redirecionando..." : "Entrar com Microsoft"}
              </button>

              <div className="border border-[#40B6A2]/30 bg-[#40B6A2]/5 rounded-sm p-3 flex gap-2">
                <Warning weight="fill" className="w-4 h-4 text-[#40B6A2] flex-shrink-0 mt-0.5" />
                <div className="text-xs text-[#b8cde2] leading-relaxed">
                  Login exclusivo para colaboradores BRFértil.
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleLocalLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-wider text-[#8aa9c9]">E-mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="input-local-email"
                  placeholder="admin@brfertil.local"
                  className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#40B6A2] focus:border-[#40B6A2]"
                />
              </div>
              <div className="space-y-2">
                <label className="text-xs uppercase tracking-wider text-[#8aa9c9]">Senha</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="input-local-password"
                  placeholder="••••••••"
                  className="w-full bg-transparent border border-[#123a5e] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#40B6A2] focus:border-[#40B6A2]"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="btn-local-login"
                className="w-full py-3 bg-[#40B6A2] text-[#061729] font-semibold text-sm rounded-sm hover:bg-[#359a89] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Lock weight="fill" className="w-4 h-4" />
                {loading ? "Autenticando..." : "Entrar"}
              </button>
              <div className="text-xs text-[#6b89a8] text-center">
                Apenas administradores. Use o SSO para demais perfis.
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
